//QuickSearch.tsx ==============================================================
import "../Util/Prototypes";

import * as AutoCompleteMUI from "./AutoCompleteMUI";
import * as Custom from "../Custom";
import * as Default from "../Default";
import * as DrawerMenu from "../Menus/DrawerMenu";
import * as GeocodeTool from "./GeocodeTool";
import * as GetSearchResults from "./GetSearchResults";
import * as GoToXY from "./GoToXY";
import * as NoRecordsFound from "./NoRecordsFound";
import * as QuickSearch from "./QuickSearch";
import * as Search from "./Search";
import * as SelectionMode from "./SelectionMode";
import * as Settings from "../Settings";
import * as Util from "../Util/Util";
import * as Util_MUI from "../Util/Util_MUI";
import * as WidgetHeading from "../Widgets/WidgetHeading";

import { Fab, Icon, Paper, TextField, Typography } from "@mui/material";

import React from "react";

const toolName: string = "Quick Search";

const all_fields: string = "All Fields";
const max_history: number = 30;

export let layerName: string = "";
export let fieldName: string = "";
export let searchText: string = "";
export let exactSearch: boolean = false;

export let layerNameDropdownValue: string = "";
export let fieldNameDropdownValue: string = "";

export let forceUpdate: boolean = false;

//Setters.
export function setSearchText(text: string) {
   searchText = text;
}
export function setForceUpdate(update: boolean) {
   forceUpdate = update;
}

interface iHist {
   layerName: string;
   fieldName: string;
   searchText: string;
}
export let history: iHist[]; //Used to build autocomplete suggestion from history.

interface iSlas {
   layerAlias: string;
   layerField: string;
}

let layerFieldValue: string = all_fields;
let layerAliasLabels: string[];
let layerFieldValues: string[];

let focusMe: boolean = true;
let initialValue: string = null;

let hist: iHist[];
let histText: string[]; // Used to remove duplicates.

//Clear this tool.
export function clearTool() {
   if (GeocodeTool.searchWidget) {
      GeocodeTool.setSearchText(GeocodeTool.searchWidget.searchTerm); //Save geocode search text.
      GeocodeTool.searchWidget.clear();
   }
}

interface iProps {
}

export function Render(props: iProps) {
   //Util.logMsg("Top of QuickSearch Render");

   //Wire up component for each time render is called, but not on refresh.
   const [initComponent, setState] = React.useState(() => {
      Search.setActiveSearchTool(toolName);
      loadSearchHistory();
      loadLayerAliasDropdown();
   });

   //Set refresh method.
   const [ignored, refresh] = React.useReducer((x: number) => x + 1, 0);

   //Component did mount.
   React.useEffect(() => {

      //Component will unmount.
      return () => {
         forceUpdate = false;
      };
   }, []);

   //Component did update.
   React.useEffect(() => {
      forceUpdate = false;
   });

   //Close this tool.
   function close() {
      DrawerMenu.closeTool();
   }

   //Layer and field name to search changed.
   function searchFieldChanged(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) {
      layerFieldValue = event.target.value;
      layerNameDropdownValue = "";
      fieldNameDropdownValue = "";

      if (layerFieldValue !== all_fields) {
         const sa: string[] = layerFieldValue.split("|");
         layerNameDropdownValue = sa[0];
         fieldNameDropdownValue = sa[1];
      }

      searchText = "";
      forceUpdate = true;
      initialValue = "";

      refresh(); //To refresh search fields dropdown.
   }

   //History changed event.
   function historyChanged(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) {
      const value: string = event.target.value;

      if (value !== "History") {
         const histRec = JSON.parse(value) as iHist;
         layerName = histRec.layerName;
         fieldName = histRec.fieldName;
         searchText = histRec.searchText;
         exactSearch = true;
         forceUpdate = true;
         initialValue = histRec.searchText;

         search();
      }
   }

   //Autocomplete enter key pressed or suggestion was selected.
   function autoCompleteSelected(selectedValue: string, exactSearch2: boolean): void {
      const sa: string[] = selectedValue.split("|");
      layerName = sa[0];
      fieldName = sa[1];
      searchText = sa[2];
      exactSearch = exactSearch2;

      search();
   }

   //Search button click event.
   function handleSearch() {
      if (QuickSearch.searchText.trim() !== "~^~") { //Set to blank to disable select all records functionality.
         layerName = QuickSearch.layerNameDropdownValue;
         fieldName = QuickSearch.fieldNameDropdownValue;
         exactSearch = false;
         search();
      }
   }

   //Process search.
   function search() {
      if (GoToXY.isXY(QuickSearch.searchText)) {
         GoToXY.zoomToXY(QuickSearch.searchText);

      } else {
         if (Settings.insertDashesOnPIN) {
            searchText = Custom.addDashesToPIN(QuickSearch.searchText); //Insert dashes on pin.
         }

         addToSearchHistory();
         GetSearchResults.init();
      }
   }

   const title: string = toolName.replace("Quick ", "");
   const scrollDivID: string = toolName.replace(" ", "_") + "_Scroll";

   const jsx: JSX.Element[] = Util_MUI.buildSelectMenuItems(layerAliasLabels, layerFieldValues);
   const jsx2: JSX.Element[] = loadHistoryDropdown();

   let historyDisabled: boolean = true;
   if (jsx2.length > 1) {
      historyDisabled = false;
   }

   if (Settings.isPhone) {
      focusMe = false;
   }

   //Util.logMsg("MainMenu rendering");
   const styles: Default.iStyles = {
      dummy: { height: 1, paddingRight: 1 },
   };

   return (
      <Paper style={{ overflowX: 'hidden', maxWidth: Settings.leftPanelWidth }}>
         <WidgetHeading.Render title={title} onClose={close} />

         <div id={scrollDivID} style={Default.styles.appToolDiv}>
            <div className="app-flex-row-start" style={{ marginLeft: 4 }}>

               <TextField select style={{ width: 150, marginRight: 30, marginBottom: 15 }}
                  label="Search Field"
                  title="Filter by database field"
                  slotProps={{
                     htmlInput: { id: "all-fields-qs" },
                     inputLabel: { htmlFor: "all-fields-qs" }
                  }}
                  value={layerFieldValue}
                  onChange={searchFieldChanged}
                  variant="standard"
               >
                  {jsx}
               </TextField>

               <TextField select style={{ width: 120, marginRight: 0, marginBottom: 15 }}
                  label="Previous Search"
                  title="Search History"
                  slotProps={{
                     htmlInput: { id: "history-qs" },
                     inputLabel: { htmlFor: "history-qs" }
                  }}
                  value="History"
                  onChange={historyChanged}
                  disabled={historyDisabled}
                  variant="standard"
               >
                  {jsx2}
               </TextField>
            </div>

            <div className="app-flex-row-between" style={{ marginLeft: 4, marginRight: 7 }}>
               <AutoCompleteMUI.Render
                  initialValue={initialValue}
                  width={270}
                  backgroundColor="White"
                  highlightColor={Settings.hoverColor}
                  focusMe={focusMe}
                  toMixedCase={true}
                  onSelected={autoCompleteSelected}
               >Search
               </AutoCompleteMUI.Render>

               <div>
                  <Fab size="small" color="default" onClick={handleSearch} title="Search" aria-label="Search">
                     <Icon color="action">search</Icon>
                  </Fab>
               </div>
            </div>

            <NoRecordsFound.Render />
            <SelectionMode.Render tool="QuickSearch" />

            <div>
               <Typography variant="body2" style={{ margin: 10 }}>
                  <b><i>Tip:</i></b>&nbsp;&nbsp;To find a location by coordinates, enter values separated by a comma.
               </Typography>

               <GeocodeTool.Render />
            </div>
         </div>
      </Paper>

   ); //end return;
} //end function.

//Load quick search history.
function loadSearchHistory() {
   hist = [];
   histText = [];

   if (localStorage.Exists("QuickSearch") && localStorage.Exists("QuickSearchText")) {
      hist = localStorage.GetItem("QuickSearch") as iHist[];
      histText = localStorage.GetItem("QuickSearchText") as string[];
      //Util.logMsg('Loaded history length:' + hist.length, hist);

      if (hist.length > max_history) {
         const numToRemove: number = hist.length - max_history;
         const pos: number = hist.length - numToRemove;
         hist.splice(pos, numToRemove); //Cull the herd of searches a bit.
         histText.splice(pos, numToRemove);
         saveHistory();
         //Util.logMsg('Truncated history count to ' + hist.length);
      }
   }
}

//Save previous exact searches into local storage.
function addToSearchHistory() {
   if (QuickSearch.exactSearch) {
      const histRec: iHist = { layerName: QuickSearch.layerName, fieldName: QuickSearch.fieldName, searchText: QuickSearch.searchText };
      const histTextRec: string = QuickSearch.layerName + "|" + QuickSearch.fieldName + "|" + QuickSearch.searchText;

      const pos: number = histText.indexOf(histTextRec);
      if (pos !== -1) {
         hist.RemoveAt(pos);
         histText.RemoveAt(pos);
      }

      hist.InsertAt(0, histRec);
      histText.InsertAt(0, histTextRec);
      saveHistory();
   }
}

//Save history.
function saveHistory() {
   localStorage.SetItem("QuickSearch", hist);
   localStorage.SetItem("QuickSearchText", histText);
}

//Load history dropdown.
function loadHistoryDropdown(): JSX.Element[] {
   let i: number;

   //Load layer dash alias array.
   const labels: string[] = [];
   const values: string[] = [];

   for (i = 0; i < hist.length; i++) {
      if (QuickSearch.layerNameDropdownValue === "" || QuickSearch.layerNameDropdownValue === hist[i].layerName) {
         labels.Add(hist[i].searchText);
         values.Add(JSON.stringify(hist[i]));
      }
   }

   const jsx: JSX.Element[] = Util_MUI.buildSelectMenuItems(labels, values, "History");
   history = hist; //Save history so it can be added to autocomplete suggestions.
   return jsx;
}

//Load searchable layer dash field combo box.
function loadLayerAliasDropdown() {
   let j: number;
   let sl: iSlas;
   let field: string;
   let aliasName: string;
   let sa: string[];
   let layName: string;
   const slas: iSlas[] = [];

   //Create sorted layer dash alias/field object.
   for (const lay of Settings.layers) {

      for (j = 0; j < lay.simpleSearchFields.length; j++) {
         field = lay.simpleSearchFields[j];
         aliasName = lay.simpleSearchAliases[j];

         if (Settings.security.accessLevel >= lay.simpleSearchAccessLevels[j]) {
            sl = {
               layerAlias: lay.layerName + " - " + aliasName,
               layerField: lay.layerName + "|" + field,
            };
            slas.Add(sl);
         }
      }
   }

   //Sort the list.
   slas.sort(Util.sortArray("layerAlias"));

   //Load layer dash alias array.
   layerAliasLabels = [];
   layerFieldValues = [];
   layerAliasLabels.Add(all_fields);
   layerFieldValues.Add(all_fields);

   //Add highlighting brackets.
   for (const sla of slas) {
      sa = sla.layerAlias.split(" - ");
      layName = sa[0];
      aliasName = sa[1];

      if (layName === Search.activeLayer) {
         aliasName = " - [" + aliasName + "]";
      } else {
         aliasName = " - " + aliasName;
      }

      layerAliasLabels.Add(layName + aliasName);
      layerFieldValues.Add(sla.layerField);
   }
}
